import { useState, useCallback, useRef } from "react";
import { FiPlus } from "react-icons/fi";
import { useDisclosure, useToast, Text } from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { evaluationPanelSteps } from "guides/features/Evaluation";
import { Button } from "newui/components/Button";
import Modal from "newui/components/ui/Modal/Modal";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useEvaluationPanel } from "../../../hooks/useEvaluationPanel";
import type { PanelMode, SendInvitationsPayload } from "../../../types/panel.type";
import { PanelModeSelector } from "./PanelModeSelector";
import { PanelMemberTable } from "./PanelMemberTable";
import { AddPanelMemberRow } from "./AddPanelMemberRow";
import { InviteConfirmationDrawer } from "../../InviteConfirmationDrawer";
import { PanelMemberSummary } from "./PanelMemberSummary";
import { useCurrentCompany, useCurrentUserId } from "hooks/auth";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { WarningBox } from "newui/components/IconBox";

interface EvaluationPanelStepProps {
	evalId?: number;
	isLoading?: boolean;
	isRfxLead?: boolean;
	rfxData: Rfx;
	savedPanelMode?: PanelMode;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onPanelModeChange?: (mode: PanelMode) => void;
}

export const EvaluationPanelStep: React.FC<EvaluationPanelStepProps> = ({
	rfxData,
	evalId,
	isLoading = false,
	isRfxLead = false,
	savedPanelMode,
	onPanelModeChange,
	onBeforeNext,
}) => {
	const rfxId = rfxData?.id;
	const hasAIAccess = useHasAIEvaluationAccess();
	const currentUserId = useCurrentUserId();
	const { id: companyId, name: companyName } = useCurrentCompany();
	const rfxTitle = rfxData?.name ?? "";
	const toast = useToast();

	const {
		panelMembers,
		panelMode,
		isLoading: isPanelLoading,
		error,
		hasInvitedMembers,
		hasPendingMembers,
		addMember,
		fetchPanel,
		sendInvitations,
		setMode,
	} = useEvaluationPanel({ rfxId, initialMode: savedPanelMode ?? (hasAIAccess ? undefined : "manual_panel") });

	const {
		isOpen: isDrawerOpen,
		onOpen: onDrawerOpen,
		onClose: onDrawerClose,
	} = useDisclosure();

	const [selectedInviteIds, setSelectedInviteIds] = useState<number[]>([]);
	const [isAddingMember, setIsAddingMember] = useState(false);
	const [showPendingWarning, setShowPendingWarning] = useState(false);
	const pendingResolveRef = useRef<((proceed: boolean) => void) | null>(null);

	const handleModeChange = useCallback(
		(mode: PanelMode) => {
			setMode(mode);
			if (onPanelModeChange) {
				onPanelModeChange(mode);
			}
		},
		[setMode, onPanelModeChange]
	);

	const pendingMembers = panelMembers.filter((m) => m.status === "pending");
	const pendingMemberIds = pendingMembers.map((m) => m.id);
	const pendingMemberNames = pendingMembers.map((m) => m.name || m.email);

	const handleToggleSelect = useCallback((memberId: number) => {
		setSelectedInviteIds((prev) => {
			if (prev.includes(memberId)) {
				return prev.filter((id) => id !== memberId);
			}
			return [...prev, memberId];
		});
	}, []);

	const handleToggleSelectAll = useCallback((selectAll: boolean) => {
		setSelectedInviteIds(selectAll ? pendingMemberIds : []);
	}, [pendingMemberIds]);

	const handleSendInvitations = useCallback(
		async (payload: SendInvitationsPayload) => {
			const result = await sendInvitations(payload);
			setSelectedInviteIds([]);
			return result;
		},
		[sendInvitations]
	);

	const checkBeforeNext = useCallback(async (): Promise<boolean> => {
		const isPanelMode = panelMode === "manual_panel" || panelMode === "ai_panel";
		if (isPanelMode && panelMembers.length === 0) {
			toast({
				title: "No Panel Members",
				description: "Please invite Evaluation panel or select 'score myself' to continue.",
				status: "warning",
				duration: 5000,
				isClosable: true,
			});
			return false;
		}
		if (!hasPendingMembers) {
			return true;
		}
		return new Promise<boolean>((resolve) => {
			pendingResolveRef.current = resolve;
			setShowPendingWarning(true);
		});
	}, [hasPendingMembers, panelMode, panelMembers.length, toast]);

	const handleSendAndContinue = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(false);
			pendingResolveRef.current = null;
		}
		onDrawerOpen();
	};

	const handleWarningContinue = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(true);
			pendingResolveRef.current = null;
		}
	};

	const handleWarningCancel = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(false);
			pendingResolveRef.current = null;
		}
	};

	// Register the before-next check with parent
	if (onBeforeNext) {
		onBeforeNext(checkBeforeNext);
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	const showMemberManagement = panelMode === "manual_panel" || panelMode === "ai_panel";

	const panelInstructions = [
		"Choose whether to evaluate submissions yourself (solo) or assemble an evaluation panel.",
		...(showMemberManagement ? [
			"Add panel members by name or email. Members will receive an invitation to join the evaluation.",
			"You can send invitations now or continue and send them later — uninvited members will be prompted before proceeding.",
		] : []),
	];

	if (!isRfxLead) {
		return (
			<div data-testid="evaluation-panel-step" className="space-y-2">
				<PanelMemberSummary members={panelMembers} currentUserId={currentUserId} />
			</div>
		);
	}

	return (
		<div data-testid="evaluation-panel-step">
			{isRfxLead && (
				<div className="mb-6">
					<InstructionsInfoBox instructions={panelInstructions} guideSteps={evaluationPanelSteps} />
				</div>
			)}

			{hasAIAccess && (
				<PanelModeSelector
					selectedMode={panelMode}
					hasAIAccess={hasAIAccess}
					isRfxLead={isRfxLead}
					onSelectMode={handleModeChange}
					disabled={!isRfxLead || hasInvitedMembers}
				/>
			)}

			{showMemberManagement && (
				<div className="space-y-6">
					<PanelMemberTable
						members={panelMembers}
						rfxId={rfxId}
						evalId={evalId ?? 0}
						canManage={isRfxLead}
						onMemberChanged={fetchPanel}
						selectedIds={selectedInviteIds}
						onToggleSelect={isRfxLead ? handleToggleSelect : undefined}
						onToggleSelectAll={isRfxLead ? handleToggleSelectAll : undefined}
					/>
					{isRfxLead && isAddingMember && (
						<AddPanelMemberRow
							companyId={companyId}
							currentUserId={currentUserId}
							panelMembers={panelMembers}
							panelMode={panelMode}
							rfxId={rfxId}
							onAddMember={addMember}
							onClose={() => setIsAddingMember(false)}
						/>
					)}
					{isRfxLead && showMemberManagement && !isAddingMember && (
						<div className="mt-6 flex items-center gap-2">
							<Button
								btntype="secondary"
								onClick={() => setIsAddingMember(true)}
								data-testid="add-panel-member-btn"
							>
								<span className="flex items-center gap-1">
									<FiPlus size={14} />
									Add Panel Member
								</span>
							</Button>
							<Button
								btntype="primary"
								onClick={onDrawerOpen}
								disabled={selectedInviteIds.length === 0}
								data-testid="proceed-invitations-btn"
							>
								Send Invitations{selectedInviteIds.length > 0 ? ` (${selectedInviteIds.length})` : ""}
							</Button>
						</div>
					)}
				</div>
			)}

			{error && (
				<p className="mt-3 text-sm text-red-600" data-testid="panel-error">
					{error}
				</p>
			)}

			{showMemberManagement && (
				<InviteConfirmationDrawer
					isOpen={isDrawerOpen}
					onClose={onDrawerClose}
					rfxId={rfxId}
					panelMembers={panelMembers}
					selectedMemberIds={selectedInviteIds}
					onSendInvitations={handleSendInvitations}
					companyName={companyName}
					rfxTitle={rfxTitle}
				/>
			)}

			<Modal
				isOpen={showPendingWarning}
				onClose={handleWarningCancel}
				title="Unsent Invitations"
				iconType="warning"
				size="md"
				closeOnEsc
				closeOnOverlayClick
				customFooter={
					<div className="flex gap-2">
						<Button btntype="primary" onClick={handleSendAndContinue}>
							Send Invites
						</Button>
						<Button btntype="secondary" onClick={handleWarningContinue}>
							Continue without sending
						</Button>
						<Button btntype="secondary" onClick={handleWarningCancel}>
							Cancel
						</Button>
					</div>
				}
			>
				<Text color="gray.600">
					The following panel members have not been sent their invitations yet:
				</Text>
				<ul className="list-disc pl-5 text-sm text-gray-700">
					{pendingMemberNames.map((name) => (
						<li key={name}>{name}</li>
					))}
				</ul>
				<WarningBox>
					<div className="mb-2">
						They will not be able to participate in the evaluation until
						invitations are sent.
					</div>
					<div>Are you sure you want to continue?</div>
				</WarningBox>
			</Modal>
		</div>
	);
};
